import { useQuery } from "@tanstack/react-query";

import { getEventsByStatus } from "@/services";

import { EventStatus } from "../types";
import { getEventStatusRefetchInterval } from "../utils/matchOdds";
import { useSportSocketConnected } from "../utils/socketStatus";

/** The statuses that have a page of their own: live, the next day, the rest. */
export type ListedEventStatus = Extract<EventStatus, "live" | "next24" | "upcoming">;

/**
 * Every event in one status, grouped by sport and league.
 *
 * Kept fresh at the status's own pace: every ten seconds for live play, every
 * minute for the next day, every five for the rest. The live list is pushed
 * over the /sport socket, so its poll pauses while the socket is up and comes
 * back as the fallback when it drops. `fetch: false` only reads what a page
 * has loaded.
 */
export const useEventsByStatus = (eventStatus: ListedEventStatus, { fetch = true } = {}) => {
  const pushed = useSportSocketConnected() && eventStatus === "live";
  return useQuery({
    queryKey: ["events-by-status", eventStatus],
    queryFn: () => getEventsByStatus(eventStatus),
    enabled: fetch,
    refetchInterval: pushed ? false : getEventStatusRefetchInterval(eventStatus),
  });
};
